import { forwardRef, useImperativeHandle, useContext, useMemo } from 'react';

import { combineURL, EllipsisSpan, Table } from '@maxtropy/components';

import Button from '@/components/Button';

import { Space, Select, Form, Row, Col, App } from 'antd';

import styles from './index.module.scss';

import { DeviceStatus, DeviceStatusDisplay } from '@/api/type';

import { padStart } from '@/pages/Bsa/utils';
import { ChildDataRef, CreateStatus, DataValueContext, TemplateType } from '../const';
import { useRequest } from 'ahooks';
import { apiV2NeoBsaDeviceCreateNeoBsaRackPost, apiV2NeoBsaDeviceListNeoBsaRackPost } from '@maxtropy/dmes-apis-v2';
import CreatingModal from '@/pages/BsaDataCollectionConfigNew/components/CreatingModal';
import { DoubleRightOutlined } from '@ant-design/icons';
import { useGetDeviceTypes, useGetModalsList } from '../selectListHooks';
import DeviceModelSelect from './DeviceModelSelect';
import { useChangeStatusApi, useGetCreateStatus, useGetTempateInfo, useSaveTemplateApi } from '../commonApiHooks';

const columns = [
  {
    title: '设备编号',
    dataIndex: 'deviceCode',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '对应PCS',
    dataIndex: 'pcsName',
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '电池堆编号',
    dataIndex: 'stackSequence',
    render: (v: number) => <EllipsisSpan value={padStart(v)} />,
  },
  {
    title: '电池簇编号',
    dataIndex: 'sequence',
    ellipsis: { showTitle: true },
    render: (v: number) => <EllipsisSpan value={padStart(v)} />,
  },
  {
    title: '类目',
    dataIndex: 'typeName',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '型号',
    dataIndex: 'modelName',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '电池簇能量',
    dataIndex: 'energy',
    ellipsis: { showTitle: true },
    render: (v: string, record: any) => (
      <EllipsisSpan value={v ? `${v ?? ''} ${record.energyUnit ?? ''}` : undefined} />
    ),
  },
  {
    title: '额定电压',
    dataIndex: 'voltage',
    ellipsis: { showTitle: true },
    render: (v: string, record: any) => (
      <EllipsisSpan value={v ? `${v ?? ''} ${record.voltageUnit ?? ''}` : undefined} />
    ),
  },
  {
    title: '设备状态',
    dataIndex: 'deviceStatus',
    ellipsis: { showTitle: true },
    render: (value: DeviceStatus) => {
      return <EllipsisSpan value={DeviceStatusDisplay[value]} />;
    },
  },
  {
    title: '操作',
    width: 180,
    fixed: 'right' as const,
    render: (v: undefined, record: any) => (
      <a
        target="_blank"
        href={combineURL(window.IOTPLATFORMORIGIN, `/device/manage/device/${record.deviceId}/detail`)}
        rel="noreferrer"
      >
        查看详情
      </a>
    ),
  },
];

const formLayout = {
  labelCol: { span: 24 },
  wrapperCol: { span: 22 },
};

const RackForm = forwardRef<ChildDataRef>((props, ref) => {
  const { bsaId, refresh, data: detail, isView } = useContext(DataValueContext);

  const { message } = App.useApp();
  useImperativeHandle(ref, () => ({
    saveForm,
    saveDraft,
  }));
  const [form] = Form.useForm();

  const deviceTypeId = Form.useWatch('deviceTypeId', form);

  // 轮询创建的状态
  const { createStatus, startPollingCreateStatus, showModalLoading } = useGetCreateStatus(
    TemplateType.RACK,
    Number(bsaId),
    () => form.submit(),
    refresh
  );
  useGetTempateInfo(TemplateType.RACK, Number(bsaId), detail?.rackDeviceCreateStatus, form);

  const saveTemplateApi = useSaveTemplateApi();

  // 更改状态机
  const changeStatus = useChangeStatusApi(Number(bsaId), Number(detail?.actualStatus));
  const deviceTypes = useGetDeviceTypes(TemplateType.RACK, detail?.rackDeviceCreateStatus);
  const { modelList, physicalModelList } = useGetModalsList(deviceTypeId);
  // 电池簇表格
  const { data: tableList, loading: tableLoading } = useRequest(
    () => apiV2NeoBsaDeviceListNeoBsaRackPost({ neoBsaId: Number(bsaId) }).then(res => res.list),
    {
      ready: createStatus?.rackDeviceCreateStatus === CreateStatus.DONE,
    }
  );

  // 生成电池簇api
  const { runAsync: createRack, loading: createLoading } = useRequest(
    params => apiV2NeoBsaDeviceCreateNeoBsaRackPost(params),
    {
      manual: true,
    }
  );
  const onSubmit = (values: any) => {
    createRack({
      neoBsaId: Number(bsaId),
      ...values,
    }).then(() => {
      // 轮询创建状态
      startPollingCreateStatus();
    });
  };

  const saveForm = () => {
    // 状态机流转
    if (createStatus?.rackDeviceCreateStatus !== CreateStatus.DONE) {
      message.success('设备未创建完成!');
      return;
    }
    changeStatus().then(() => {
      refresh?.();
    });
  };
  // 保存簇模板
  const saveDraft = () => {
    if (createStatus?.rackDeviceCreateStatus === CreateStatus.DONE) return;
    form.validateFields().then(values => {
      saveTemplateApi({
        neoBsaId: Number(bsaId),
        type: TemplateType.RACK,
        ...values,
      }).then(() => message.success('保存草稿成功!'));
    });
  };
  // 列表是否展示
  const showTableList = useMemo(() => {
    return createStatus?.rackDeviceCreateStatus === CreateStatus.DONE || isView;
  }, [createStatus, isView]);
  return (
    <div className={styles.rack_box}>
      {!showTableList && (
        <div className={styles.form_box}>
          <div className={styles.title}>电池簇型号</div>
          <Form form={form} layout="vertical" {...formLayout} onFinish={onSubmit}>
            <Row>
              <Col span={6}>
                <Form.Item
                  name="deviceTypeId"
                  label="所属二级类目"
                  rules={[{ required: true, message: '请选择所属类目' }]}
                >
                  <Select
                    disabled={isView}
                    placeholder="请选择"
                    onChange={() => {
                      form.setFieldsValue({
                        modelName: undefined,
                      });
                    }}
                  >
                    {(deviceTypes ?? []).map(i => (
                      <Select.Option key={i.id} value={i.id}>
                        {i.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="modelName" label="型号" rules={[{ required: true, message: '请选择所属型号' }]}>
                  <DeviceModelSelect
                    disabled={!deviceTypeId || isView}
                    options={modelList?.map(i => ({ label: i.name, value: i.name }))}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="physicalModelId" label="物模型">
                  <Select
                    disabled={!deviceTypeId || isView}
                    style={{ width: '100%' }}
                    showSearch
                    allowClear
                    placeholder="请输入"
                    optionFilterProp="label"
                    options={physicalModelList?.map(i => ({ label: i.modelNo, value: i.id }))}
                  />
                </Form.Item>
              </Col>
              {!isView && (
                <Col span={6}>
                  <Form.Item label={<span></span>}>
                    <Button loading={createLoading} type="primary" htmlType="submit">
                      批量生成电池簇
                    </Button>
                  </Form.Item>
                </Col>
              )}
            </Row>
          </Form>
        </div>
      )}

      {showTableList && (
        <div className={styles.table_box}>
          <Space size={12}>
            <div className={styles.table_title}>电池簇列表</div>
            <div
              className={styles.primary_color}
              onClick={() => {
                window.open(combineURL(window.IOTPLATFORMORIGIN, `/device/manage/device`), '_blank');
              }}
            >
              <span>前往修改电池簇信息 &nbsp;</span>
              <DoubleRightOutlined style={{ fontSize: 12 }} />
            </div>
          </Space>

          <Table
            loading={tableLoading}
            columns={columns}
            rowKey="deviceId"
            dataSource={tableList}
            rowClassName={(record: any) => {
              return record.deviceStatus === DeviceStatus.INVALID ? 'device-invalid' : '';
            }}
          />
        </div>
      )}

      <CreatingModal loading={showModalLoading} content="电池簇创建中" />
    </div>
  );
});

export default RackForm;
